// Unit 08 — IPv6 Basics (Lesson + Verification)
import { predict, observe, readConfirm } from '../../ui/widgets.js';
import { star } from './topologies.js';
import { ran } from './helpers.js';
import { v6Compress, v6Expand } from '../../engine/net/addr.js';
import { slaac, routerLinkLocal } from '../../engine/net/sim.js';

const layout = {
  width: 900, height: 320,
  nodes: {
    PC1: { x: 100, y: 95, type: 'pc', sub: 'Windows 11 · SLAAC client' },
    PC2: { x: 100, y: 245, type: 'pc', sub: 'Windows 11 · SLAAC client' },
    SW1: { x: 400, y: 170, type: 'switch', sub: 'Access switch' },
    R1: { x: 720, y: 170, type: 'router', sub: 'IPv4 192.168.10.1 · IPv6 pending' },
  },
  zones: [{ x: 20, y: 25, w: 860, h: 280, label: 'DUAL-STACK LAN · 192.168.10.0/24 · 2001:db8:acad:10::/64' }],
  linkLabels: { 'R1:GigabitEthernet0/0': '2001:db8:acad:10::1/64' },
};

function compressField(full, canonical) {
  return {
    label: `Compress ${full}`, placeholder: 'shortest form',
    check: (v) => {
      const e = v6Expand(v);
      if (!e) return 'That is not a valid IPv6 address (only one :: allowed, max 4 hex digits per group).';
      if (e !== v6Expand(canonical)) return 'Valid IPv6, but it is a different address — check which zeros you removed.';
      return v.toLowerCase() === canonical || 'Same address, but not the shortest form: drop leading zeros and replace the longest run of zero groups with ::.';
    },
  };
}
const pc1Global = (rt) => slaac(rt.net, 'PC1').addrs.find((a) => a.type === 'global')?.addr;

export default {
  id: 'u08', num: '08', title: 'IPv6 Basics', type: 'Verification Activity', implemented: true,
  objective: 'Read and compress IPv6 addresses, recognise address types, enable IPv6 on a router and verify that hosts auto-configure with SLAAC.',
  network: () => star({ pcs: ['PC1', 'PC2'], ports: [1, 2], withRouter: true }),
  layout, linkLights: true,
  stages: [
    {
      id: 'learn', phase: 'LEARN', title: '128 bits, written for humans', workspace: false,
      content: `
<p>IPv6 addresses are 128 bits: eight groups of four hex digits.</p>
<pre>2001:0db8:acad:0010:0000:0000:0000:0001
└──── prefix /64 ────┘└── interface ID ──┘</pre>
<h3>Two compression rules (RFC 5952)</h3>
<ol><li>Drop <b>leading</b> zeros in each group: <code>0db8 → db8</code>, <code>0010 → 10</code>, <code>0000 → 0</code>.</li>
<li>Replace the <b>single longest</b> run of all-zero groups with <code>::</code> — only once per address.</li></ol>
<p><code>2001:db8:acad:10::1</code> is the same address as above.</p>
<h3>Address types you will meet</h3>
<table><tr><th>Type</th><th>Prefix</th><th>Purpose</th></tr>
<tr><td>Global unicast (GUA)</td><td>2000::/3</td><td>Routable, like public IPv4</td></tr>
<tr><td>Link-local (LLA)</td><td>fe80::/10</td><td>Every IPv6 interface has one; never routed; used as next-hop / gateway</td></tr>
<tr><td>Unique local (ULA)</td><td>fc00::/7</td><td>Private, like RFC 1918</td></tr>
<tr><td>Multicast</td><td>ff00::/8</td><td>Replaces broadcast (IPv6 has <b>no broadcast</b>)</td></tr>
<tr><td>Loopback</td><td>::1</td><td>This host</td></tr></table>
<h3>SLAAC</h3>
<p>A router with <code>ipv6 unicast-routing</code> sends <b>Router Advertisements</b> containing the /64 prefix. Hosts build their own address from it and use the router’s <b>link-local</b> address as their default gateway. Cisco routers build their link-local interface ID with <b>EUI-64</b>: MAC split in half, <code>fffe</code> inserted, 7th bit flipped.</p>`,
      widgets: [readConfirm({ id: 'u08-read' })],
      question: 'Why does an IPv6 host’s default gateway usually start with fe80?',
    },
    {
      id: 'understand', phase: 'UNDERSTAND', title: 'Compression drill', workspace: false,
      widgets: [observe({
        id: 'u08-drill', title: 'COMPRESS AND EXPAND',
        fields: [
          compressField('2001:0db8:0000:0000:0000:ff00:0042:8329', '2001:db8::ff00:42:8329'),
          compressField('2001:0db8:acad:0010:0000:0000:0000:0001', '2001:db8:acad:10::1'),
          compressField('fe80:0000:0000:0000:0250:79ff:fe66:6800', 'fe80::250:79ff:fe66:6800'),
          { label: 'Expand 2001:db8:0:1::a to all 32 hex digits', placeholder: 'xxxx:xxxx:…', check: (v) => v.toLowerCase() === '2001:0db8:0000:0001:0000:0000:0000:000a' || 'Write all eight groups with four digits each.' },
        ],
      })],
      hints: ['In the first address, the three middle 0000 groups are the longest zero run.'],
    },
    {
      id: 'predict', phase: 'PREDICT', title: 'Will the PCs configure themselves?', workspace: false,
      content: '<p>You are about to give R1 Gi0/0 the address <code>2001:db8:acad:10::1/64</code>. The PCs are Windows 11 with default settings.</p>',
      widgets: [predict({
        id: 'u08-pred', prompt: 'When will PC1 get a global IPv6 address automatically?',
        options: ['As soon as R1 Gi0/0 has an IPv6 address', 'Only after R1 has ipv6 unicast-routing enabled', 'Never — IPv6 always needs a DHCPv6 server', 'Only after you configure PC1 manually'],
      })],
    },
    {
      id: 'verify', phase: 'CONFIGURE → VERIFY', title: 'Enable IPv6 and watch SLAAC work', devices: ['R1', 'PC1'],
      content: `<p>Test your prediction. Check PC1 at each step with <code>ipconfig</code>.</p>
<ol><li>R1: configure <code>ipv6 address 2001:db8:acad:10::1/64</code> on Gi0/0 → check PC1.</li>
<li>R1: enable <code>ipv6 unicast-routing</code> (global configuration) → check PC1 again.</li>
<li>R1: <code>show ipv6 interface brief</code>. PC1: <code>ping 2001:db8:acad:10::1</code>.</li></ol>`,
      evidence: [
        { id: 'r1v6', text: 'R1: IPv6 interface summary shows the global address', match: ran('R1', 'show ipv6 interface brief', /2001:DB8:ACAD:10::1/) },
        { id: 'pcv6', text: 'PC1: ipconfig shows a SLAAC global IPv6 address', match: ran('PC1', ['ipconfig', 'ipconfig /all'], /IPv6 Address[ .]+: 2001:db8:acad:10:/) },
        { id: 'ping6', text: 'PC1: ping to 2001:db8:acad:10::1 succeeds', match: (ev) => ev.device === 'PC1' && ev.key === 'ping' && ev.args?.v6 && ev.result?.success && v6Compress(ev.args.target) === '2001:db8:acad:10::1' },
      ],
      widgets: [observe({
        id: 'u08-obs', title: 'IPv6 FINDINGS', intro: 'Read these from the devices.',
        fields: [
          { label: 'R1 Gi0/0 link-local address (from show ipv6 interface brief)', placeholder: 'FE80::…', check: (v, rt) => (v6Expand(v) && v6Compress(v) === routerLinkLocal(rt.net.devices.R1, 'GigabitEthernet0/0')) || 'Copy the FE80:: line listed under GigabitEthernet0/0.' },
          { label: 'PC1’s IPv6 default gateway (without the %12 zone)', check: (v, rt) => (v6Expand(v.split('%')[0]) && v6Compress(v.split('%')[0]) === slaac(rt.net, 'PC1').gw) || 'Look at the Default Gateway lines in PC1’s ipconfig.' },
          { label: 'PC1’s global IPv6 address', check: (v, rt) => (!!pc1Global(rt) && v6Expand(v) && v6Compress(v) === pc1Global(rt)) || 'PC1 has no matching global address yet — is the router advertising?' },
        ],
      })],
      tasks: [
        { id: 'routing', text: 'R1 has IPv6 unicast routing enabled', check: (rt) => rt.net.devices.R1.ipv6Routing, fail: 'Router Advertisements are not being sent.' },
        { id: 'addr', text: 'R1 Gi0/0 has 2001:db8:acad:10::1/64', check: (rt) => rt.net.devices.R1.interfaces['GigabitEthernet0/0'].ipv6.some((a) => v6Compress(a.addr) === '2001:db8:acad:10::1' && a.prefix === 64), fail: 'Gi0/0 IPv6 address missing or wrong prefix.' },
        { id: 'pc2', text: 'PC2 also auto-configured (no manual work)', check: (rt) => !!slaac(rt.net, 'PC2').addrs.find((a) => a.type === 'global'), fail: 'PC2 has no global IPv6 address.' },
      ],
      question: 'The gateway PC1 learned is R1’s link-local address, not 2001:db8:acad:10::1. Why is that safe?',
      hints: ['Interface address: <code>interface g0/0</code> → <code>ipv6 address 2001:db8:acad:10::1/64</code>.', '<code>ipv6 unicast-routing</code> is a <b>global</b> configuration command — exit the interface first (or IOS will fall back to global mode for you).', 'PowerShell ping works with IPv6 addresses directly: <code>ping 2001:db8:acad:10::1</code>.'],
    },
  ],
  review: (rt) => `<p>Prediction: <b>${rt.widget('u08-pred').data.choice || '—'}</b>. The evidence: PC1 only got a global address once R1 was <b>routing</b> IPv6 and sending Router Advertisements. No DHCP server, no manual configuration.</p>`,
};
